import type { HITLRendererProps } from '../HITLRenderer';
import { hitlStyles, cn } from './styles';

/**
 * Fallback renderer for unknown or unimplemented HITL types.
 * Displays raw data and provides basic interaction.
 */
export const FallbackRenderer: React.FC<HITLRendererProps> = ({
  response,
  onSubmit,
  onCancel,
  isSubmitting,
  compact,
}) => {
  const handleContinue = () => onSubmit('continue');

  return (
    <div className={cn(
      compact ? hitlStyles.containerCompact : hitlStyles.container,
      'bg-yellow-50 border-yellow-200'
    )}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-lg">⚠️</span>
        <h3 className="text-sm font-semibold text-yellow-800">
          Input Required
        </h3>
      </div>

      {response.message && (
        <p className="text-sm text-yellow-700 mb-3">{response.message}</p>
      )}

      {response.context && (
        <p className="text-xs text-yellow-600 mb-3">{response.context}</p>
      )}

      {/* Debug info - collapsible */}
      <details className="mb-3">
        <summary className="text-xs text-yellow-600 cursor-pointer hover:text-yellow-700">
          Debug: View raw HITL response
        </summary>
        <pre className="mt-2 p-2 bg-white rounded text-xs overflow-auto max-h-32">
          {JSON.stringify(response, null, 2)}
        </pre>
      </details>

      {/* Options if available */}
      {response.options && response.options.length > 0 && (
        <div className="space-y-2 mb-3">
          {response.options.map((option) => (
            <button
              key={option.id}
              onClick={() => onSubmit(option.id)}
              disabled={isSubmitting}
              className={cn(
                hitlStyles.optionButton,
                option.isPrimary 
                  ? 'bg-yellow-600 text-white border-yellow-600 hover:bg-yellow-700'
                  : 'bg-white border-yellow-300 text-yellow-800 hover:bg-yellow-50'
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      {/* Fallback buttons if no options */}
      {(!response.options || response.options.length === 0) && (
        <div className="flex gap-2">
          {onCancel && (
            <button
              onClick={onCancel}
              disabled={isSubmitting}
              className={cn(hitlStyles.actionButton, hitlStyles.actionButtonSecondary)}
            >
              Cancel
            </button>
          )}
          <button
            onClick={handleContinue}
            disabled={isSubmitting}
            className={cn(
              hitlStyles.actionButton,
              'flex-1 bg-yellow-600 text-white hover:bg-yellow-700'
            )}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <span className={hitlStyles.spinner} />
                Processing...
              </span>
            ) : (
              'Continue'
            )}
          </button>
        </div>
      )}
    </div>
  );
};

